

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from "framer-motion";
import { services, generateServiceSlug } from '../components/servicesData';


const Services = () => {
  const location = useLocation();
  const serviceRefs = useRef({});
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    Object.keys(services).forEach(key => {
      serviceRefs.current[key] = React.createRef();
    });
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    const hash = location.hash.substring(1);
    if (hash && serviceRefs.current[hash]?.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const element = serviceRefs.current[hash].current;
          if (element) {
            const headerOffset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          }
        });
      });
    }
  }, [location, isMounted]);
  const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2, // adjust timing between each line
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};


  return (
    <div className="bg-gray-50">
      {/* Header Banner */}
      <section className="relative w-full">
        <img
          src="https://www.elitasrcs.com/public/storage/webi1-03.jpg"
          alt="Our Services"
          className="w-full h-[250px] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#007c82]/80 to-[#00b3b0]/80 flex items-center justify-center">
          <h1 className="text-4xl font-bold text-white drop-shadow-lg">Our Services</h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="bg-[#007c82] py-3">
        <div className="container mx-auto px-4 text-sm text-white">
          <ul className="flex flex-wrap items-center space-x-2">
            <li><Link to="/" className="hover:underline">Home</Link></li>
            <li>&raquo;</li>
            <li><Link to="/services" className="hover:underline">Services</Link></li>
            {location.hash && (
              <>
                <li>&raquo;</li>
                <li className="truncate max-w-xs">
                  {services[location.hash.substring(1)]?.title}
                </li>
              </>
            )}
          </ul>
        </div>
      </section>

      {/* Service Cards */}
      <section className="py-12">
        <div className="container mx-auto px-8 flex flex-col gap-10">
          {Object.entries(services).map(([slug, service], index) => {
            const isEven = index % 2 === 0;
            return (
              <div
                key={slug}
                ref={serviceRefs.current[slug]}
                id={slug}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden transition-transform hover:scale-[1.01]"
              >
                {/* Title */}
                <div className="bg-[#00a09c] px-6 py-3">
                  <h2 className="text-white text-xl font-semibold flex items-center gap-2">
                    <span>{service.icon}</span> {service.title}
                  </h2>
                </div>

                {/* Alternating Content Layout */}
                <div className={`p-6 flex flex-col md:flex-row ${!isEven ? 'md:flex-row-reverse' : ''} items-center gap-8`}>
                  
                  {/* Text with Animation */}
                  <motion.div
                    className="md:w-1/2"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: false, amount: 0.3 }}
                  >
                    <motion.ul
                      className="space-y-2 text-sm text-gray-700 list-disc list-inside"
                      variants={listVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: false, amount: 0.2 }}
                    >
                      {service.content.map((point, i) => (
                        <motion.li key={i} variants={itemVariants}>
                          {point}
                        </motion.li>
                      ))}
                    </motion.ul>
                  </motion.div>

                  {/* Image with Animation (delayed) */}
                  <motion.div
                    className="md:w-1/2 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    viewport={{ once: false, amount: 0.1 }}
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="rounded-xl shadow-md w-full max-w-sm mx-auto"
                      loading="lazy"
                    />
                  </motion.div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      
    </div>
  );
};

export default Services;